import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { isBhooklyNativeServerApp, isEmbeddedServerMode } from '@/lib/bhooklyNative'
import { getStoredUserRole } from '@/lib/staff-roles'
import {
  Pencil,
  ReceiptText,
  ArrowRightLeft,
  CreditCard,
  Printer,
  XCircle,
  Users,
  Clock,
  X,
  ScanLine,
  UserCheck,
} from 'lucide-react'
import apiClient from '@/api/client'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import type { DiningTable, Order, Payment } from '@/types'
import { ServerScanLoyaltyDialog } from './ServerScanLoyaltyDialog'
import { CustomerBillOverlay } from './CustomerBillOverlay'
import { useCurrency } from '@/contexts/CurrencyContext'
import { useEnabledOrderTypes } from '@/hooks/useEnabledOrderTypes'
import { displayLabelForOrderType } from '@/lib/orderTypeSettings'
import {
  parseReceiptSettings,
  printUnpaidCustomerBill,
  getCashierNameFromStorage,
  getServerNameFromOrder,
  type CustomerReceiptSettings,
  type UnpaidTenderTotals,
} from '@/lib/printCustomerReceipt'
import { loadServerPrebill } from '@/lib/counterPricingQuery'
import {
  SEATED_EMPTY_STALE_MINUTES,
  formatDuration,
  isSeatedEmpty,
  minutesSince,
  turnoverStatus,
  TURNOVER_STATUS_STYLES,
} from '@/lib/tableTurnover'
import { toastHelpers } from '@/lib/toast-helpers'
import { invalidateTables } from '@/lib/tablesCache'

type Props = {
  /** The selected occupied table (must carry current_order). */
  table: DiningTable
  /** Shared epoch-ms clock driving the live seated / since-placed timers. */
  now: number
  /** All tables — the free ones are the Move destinations. */
  tables: DiningTable[]
  /** Enter the ordering workspace to add / modify items on this table's order. */
  onEditOrder: (table: DiningTable) => void
  /** Dismiss the rail (deselect the table). */
  onClose: () => void
  /**
   * 'rail' (default) — the narrow vertical column layout for the landscape
   * right rail and the phone drawer. 'sheet' — a two-column layout (bill on
   * the left, actions on the right) for the portrait-tablet bottom panel,
   * where the panel is wide but short.
   */
  variant?: 'rail' | 'sheet'
}

/** Sum of completed payments on an order. */
function paidTotal(payments: Payment[] | undefined): number {
  return (payments ?? [])
    .filter((p) => p.status === 'completed')
    .reduce((s, p) => s + Number(p.amount || 0), 0)
}

/** One row in the bill breakdown. */
function BillRow({
  label,
  value,
  strong,
  muted,
}: {
  label: string
  value: string
  strong?: boolean
  muted?: boolean
}) {
  return (
    <div
      className={`flex items-center justify-between ${
        strong ? 'text-base font-bold text-foreground' : 'text-sm'
      } ${muted ? 'text-muted-foreground' : ''}`}
    >
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  )
}

/**
 * Competitor-style left action rail for a selected occupied table. Shows the
 * live bill breakdown + one-click actions (Edit / View / Move / Settle / Print
 * Bill / Release). All actions reuse existing backend endpoints — no new API.
 */
export function ServerTableActionRail({ table, now, tables, onEditOrder, onClose, variant = 'rail' }: Props) {
  const { formatCurrency } = useCurrency()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { raw: orderTypesRaw } = useEnabledOrderTypes()

  const [moveOpen, setMoveOpen] = useState(false)
  const [printBusy, setPrintBusy] = useState(false)
  const [scanOpen, setScanOpen] = useState(false)
  const [billBusy, setBillBusy] = useState(false)
  // Loaded snapshot behind the full-screen customer bill: a fresh order + the
  // receipt settings + the server-priced tender totals, assembled by
  // handleViewBill exactly the way Print Bill assembles its data.
  const [bill, setBill] = useState<{
    order: Order
    cfg: CustomerReceiptSettings
    /** F-MM-N28: server-priced per tender; null = tender not offered. */
    cash: UnpaidTenderTotals | null
    card: UnpaidTenderTotals | null
    orderTypeLabel: string | null
  } | null>(null)

  const summary = table.current_order ?? null
  const orderId = summary?.id ?? null

  // Full order detail — needed for the tax/discount breakdown, item list, and
  // outstanding balance (the /tables summary only carries the grand total).
  const { data: order } = useQuery({
    queryKey: ['order', orderId],
    queryFn: async () => {
      const res = await apiClient.getOrder(orderId!)
      return res.success && res.data ? res.data : null
    },
    enabled: !!orderId,
  })


  const outstanding = order ? Number(order.total_amount) - paidTotal(order.payments) : Number(summary?.total_amount ?? 0)
  const nonVoidedItems = (order?.items ?? []).filter((i) => i.status !== 'voided')
  const isEmptyTab = order != null && nonVoidedItems.length === 0

  // Seated·no-order (blue tile) — drive the badge off the same summary payload
  // the floor tile uses, so an order whose lines were all voided reads "SEATED"
  // here instead of a stale "PREPARING".
  const seatedEmpty = isSeatedEmpty(summary)

  // F-FE-N61. Prefer the full order (fresh after an attach) and fall back to
  // the tables summary so the name is present on first paint, before the
  // order query resolves.
  const attachedGuestName = order?.customer_name || summary?.customer_name || null

  const seatAnchor = summary?.table_opened_at ?? summary?.created_at
  const seatedMins = seatAnchor ? minutesSince(seatAnchor, now) : 0
  const placedMins = summary?.created_at ? minutesSince(summary.created_at, now) : 0
  const seatedStyle = TURNOVER_STATUS_STYLES[turnoverStatus(seatedMins)]

  const reassignMutation = useMutation({
    mutationFn: (destTableId: string) =>
      apiClient.reassignCounterOrderTable(orderId!, { table_id: destTableId }),
    onSuccess: (res) => {
      if (res.success) {
        toastHelpers.success('Table moved', 'The order was moved to the new table.')
        invalidateTables(queryClient)
        setMoveOpen(false)
        onClose()
      } else {
        toastHelpers.error('Move failed', res.message || 'Could not move the order.')
      }
    },
    // Surface the backend's real, stable-coded reason (apiClient throws an
    // ApiClientError carrying .message/.status/.code on any non-2xx) instead of
    // a generic string — matches CounterInterface's cancel/reassign handlers.
    onError: (e: Error) => toastHelpers.error('Move failed', e.message || 'Could not move the order.'),
  })

  const releaseMutation = useMutation({
    mutationFn: () => apiClient.cancelCounterOpenTab(orderId!),
    onSuccess: (res) => {
      if (res.success) {
        toastHelpers.success('Table released', 'The empty tab was cleared.')
        invalidateTables(queryClient)
        onClose()
      } else {
        toastHelpers.error('Release failed', res.message || 'Could not release the table.')
      }
    },
    // Non-2xx throws an ApiClientError with the backend's real message
    // ("Cannot cancel after items were sent to the kitchen", 401 proxy notes,
    // etc.). Show it — the hardcoded string hid every genuine failure reason.
    onError: (e: Error) => toastHelpers.error('Release failed', e.message || 'Could not release the table.'),
  })

  const handleSettle = () => {
    // Hand off to the Counter, which owns the payments flow. It loads this
    // table's active order on mount (see the tableId search param) and opens
    // checkout. After payment the table auto-frees — no manual "end sitting".
    navigate({ to: '/admin/counter', search: { tableId: table.id } })
  }

  const handlePrintBill = async () => {
    if (!orderId) return
    setPrintBusy(true)
    try {
      const fresh = await apiClient.getOrder(orderId)
      if (!fresh.success || !fresh.data) {
        toastHelpers.error('Print bill', fresh.message || 'Could not load the order.')
        return
      }
      const o = fresh.data
      if ((o.payments ?? []).some((p) => p.status === 'completed')) {
        toastHelpers.error('Print bill', 'Not available after a payment was recorded — settle at the counter.')
        return
      }
      const settingsRes = await queryClient.fetchQuery({
        queryKey: ['settings', 'all'],
        queryFn: () => apiClient.getAllSettings(),
      })
      if (!settingsRes.success || !settingsRes.data) {
        toastHelpers.error('Print bill', 'Could not load receipt settings.')
        return
      }
      const cfg = parseReceiptSettings(settingsRes.data as Record<string, unknown>)
      // F-MM-N28: priced by the server's checkout recompute — never from the
      // local pricing query, whose empty state falls back to 10% service.
      const prebill = await loadServerPrebill(o.id)
      if (!prebill.ok) {
        toastHelpers.error('Print bill', `${prebill.reason} — bill not printed.`)
        return
      }
      // A non-fiscal pre-check (proforma). Must NOT touch MarkPraInvoicePrinted.
      const { printed } = printUnpaidCustomerBill(
        o,
        cfg,
        { serverTotals: { cash: prebill.cash, card: prebill.card } },
        {
          cashierName: getCashierNameFromStorage(),
          serverName: getServerNameFromOrder(o),
          orderTypeLabel: displayLabelForOrderType(o.order_type, orderTypesRaw),
          formatAmount: formatCurrency,
          paidAt: new Date(),
        },
      )
      if (!printed) {
        // F-FE-N106: the same correction F-FE-N104 made on the counter, which
        // this rail was missed out of. There is no popup to allow on the
        // tablet — telling a waiter to change a browser setting that does not
        // exist on their device is worse than saying nothing.
        toastHelpers.error(
          'Print bill',
          isBhooklyNativeServerApp()
            ? 'Could not reach the printer. Check the receipt printer address in Settings.'
            : 'Popup blocked — allow popups for this site.',
        )
      }
    } finally {
      setPrintBusy(false)
    }
  }

  // Show the branded on-screen bill (the paper proforma's full-screen twin,
  // for handing the tablet to the customer). Same data recipe and the same
  // guard as Print Bill: never after a payment was recorded.
  const handleViewBill = async () => {
    if (!orderId) return
    setBillBusy(true)
    try {
      const fresh = await apiClient.getOrder(orderId)
      if (!fresh.success || !fresh.data) {
        toastHelpers.error('View bill', fresh.message || 'Could not load the order.')
        return
      }
      const o = fresh.data
      if ((o.payments ?? []).some((p) => p.status === 'completed')) {
        toastHelpers.error('View bill', 'Not available after a payment was recorded — settle at the counter.')
        return
      }
      const settingsRes = await queryClient.fetchQuery({
        queryKey: ['settings', 'all'],
        queryFn: () => apiClient.getAllSettings(),
      })
      if (!settingsRes.success || !settingsRes.data) {
        toastHelpers.error('View bill', 'Could not load receipt settings.')
        return
      }
      const cfg = parseReceiptSettings(settingsRes.data as Record<string, unknown>)
      // F-MM-N28: the customer reads this screen and pays from it — same
      // server-priced figures as the printed bill, no local fallback.
      const prebill = await loadServerPrebill(o.id)
      if (!prebill.ok) {
        toastHelpers.error('View bill', `${prebill.reason} — bill not shown.`)
        return
      }
      setBill({
        order: o,
        cfg,
        cash: prebill.cash,
        card: prebill.card,
        orderTypeLabel: displayLabelForOrderType(o.order_type, orderTypesRaw) ?? null,
      })
    } finally {
      setBillBusy(false)
    }
  }

  // Server role + Bhookly Server APK: ordering only — checkout stays at counter.
  const hideCheckout = isEmbeddedServerMode() || getStoredUserRole() === 'server'

  if (!summary) return null

  const freeTables = tables.filter((t) => !(t.has_active_order ?? t.is_occupied) && t.id !== table.id)

  const headerBlock = (
    <div className="shrink-0 border-b border-border px-4 py-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-foreground">Table {table.table_number}</span>
              <Badge variant="secondary" className="text-[10px]">
                {seatedEmpty ? 'SEATED' : summary.status?.toUpperCase() || 'ACTIVE'}
              </Badge>
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              #{summary.order_number}
              {summary.guest_count > 0 && (
                <span className="ml-1 inline-flex items-center gap-0.5">
                  · <Users className="h-3 w-3" /> {summary.guest_count} guests
                </span>
              )}
              {/* F-FE-N61: the attached loyalty guest. Shown here rather than
                  buried in the bill because it is what makes the "Add Loyalty
                  Guest" action below discoverable — a server sees the slot,
                  then sees it filled. */}
              {attachedGuestName && (
                <span className="ml-1 inline-flex items-center gap-0.5 font-medium text-foreground">
                  · <UserCheck className="h-3 w-3" /> {attachedGuestName}
                </span>
              )}
            </div>
          </div>
          <Button variant="ghost" size="sm" className="h-7 w-7 shrink-0 p-0" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Live timers */}
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className={`rounded-lg border-l-4 ${seatedStyle.border} bg-muted/50/60 px-2.5 py-1.5`}>
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
              <Clock className="h-3 w-3" /> Seated
            </div>
            <div className={`text-sm font-semibold tabular-nums ${seatedStyle.text}`}>{formatDuration(seatedMins)}</div>
          </div>
          <div className="rounded-lg bg-muted/50/60 px-2.5 py-1.5">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Since order</div>
            <div className="text-sm font-semibold tabular-nums text-foreground">
              {formatDuration(placedMins)}
            </div>
          </div>
        </div>
    </div>
  )

  const billBlock = (
    <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {order ? (
          <div className="space-y-1.5">
            <BillRow label="Price" value={formatCurrency(Number(order.subtotal || 0))} />
            {Number(order.discount_amount || 0) > 0 && (
              <BillRow label="Discount" value={`- ${formatCurrency(Number(order.discount_amount))}`} muted />
            )}
            {Number(order.tax_amount || 0) > 0 && (
              <BillRow label="Tax / GST" value={formatCurrency(Number(order.tax_amount))} muted />
            )}
            {Number(order.service_charge_amount || 0) > 0 && (
              <BillRow label="Service charge" value={formatCurrency(Number(order.service_charge_amount))} muted />
            )}
            {Number(order.delivery_fee_amount || 0) > 0 && (
              <BillRow label="Delivery" value={formatCurrency(Number(order.delivery_fee_amount))} muted />
            )}
            <div className="my-1 border-t border-dashed border-border" />
            <BillRow label="Grand Total" value={formatCurrency(Number(order.total_amount || 0))} strong />
            {outstanding > 0 && outstanding !== Number(order.total_amount || 0) && (
              <BillRow label="Outstanding" value={formatCurrency(outstanding)} muted />
            )}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">Loading bill…</div>
        )}
    </div>
  )

  const actionsBlock = (
    <div
      className={cn(
        'grid shrink-0 grid-cols-2 gap-2 p-3',
        variant === 'sheet' ? 'content-start' : 'border-t border-border',
      )}
    >
        <Button variant="outline" className="justify-start" onClick={() => onEditOrder(table)}>
          <Pencil className="mr-2 h-4 w-4" /> Edit Order
        </Button>
        <Button
          variant="outline"
          className="justify-start"
          onClick={handleViewBill}
          disabled={billBusy || isEmptyTab}
        >
          <ReceiptText className="mr-2 h-4 w-4" /> View Bill
        </Button>
        <Button
          variant="outline"
          className="justify-start"
          onClick={() => setMoveOpen(true)}
          disabled={reassignMutation.isPending}
        >
          <ArrowRightLeft className="mr-2 h-4 w-4" /> Move
        </Button>
        <Button
          variant="outline"
          className="justify-start"
          onClick={handlePrintBill}
          disabled={printBusy || isEmptyTab}
        >
          <Printer className="mr-2 h-4 w-4" /> Print Bill
        </Button>
        {/* F-FE-N61 — loyalty attach for the table already in context. No
            order picker: `orderId` is the bill on screen, which is the whole
            reason this lives here instead of behind a top-level button.
            Deliberately NOT disabled on an empty tab — a guest hands over
            their card as they sit down, before anything is ordered. */}
        <Button
          variant="outline"
          className="col-span-2 justify-start"
          onClick={() => setScanOpen(true)}
          disabled={!orderId}
        >
          <ScanLine className="mr-2 h-4 w-4" />
          {attachedGuestName ? 'Change Loyalty Guest' : 'Add Loyalty Guest'}
        </Button>
        {isEmptyTab ? (
          <>
            {seatedMins >= SEATED_EMPTY_STALE_MINUTES && (
              <p className="col-span-2 text-xs text-amber-700 dark:text-amber-400">
                Seated {formatDuration(seatedMins)} with no order — release the table if the party left.
              </p>
            )}
            <Button
              variant="outline"
              className="col-span-2 justify-center border-red-200 text-red-600 hover:bg-danger-surface dark:border-red-900/60 dark:hover:bg-red-950/40"
              onClick={() => releaseMutation.mutate()}
              disabled={releaseMutation.isPending}
            >
              <XCircle className="mr-2 h-4 w-4" /> Release Table
            </Button>
          </>
        ) : hideCheckout ? (
          <p className="col-span-2 text-center text-xs text-muted-foreground">
            Settle / checkout is done at the counter — this tablet is ordering only.
          </p>
        ) : (
          <Button className="col-span-2 justify-center bg-emerald-600 hover:bg-emerald-700" onClick={handleSettle}>
            <CreditCard className="mr-2 h-4 w-4" /> Settle
          </Button>
        )}
    </div>
  )

  const dialogs = (
    <>
      {/* Customer-facing branded bill (replaces the old View Order dialog —
          it shows everything that did, presentably). */}
      {bill && (
        <CustomerBillOverlay
          order={bill.order}
          cfg={bill.cfg}
          cash={bill.cash}
          card={bill.card}
          orderTypeLabel={bill.orderTypeLabel}
          onClose={() => setBill(null)}
        />
      )}

      {/* Loyalty attach (F-FE-N61) — mounted only while open so the camera is
          never held by a hidden dialog on a table the server has moved on from. */}
      {scanOpen && orderId && summary && (
        <ServerScanLoyaltyDialog
          open={scanOpen}
          onOpenChange={setScanOpen}
          tableNumber={table.table_number}
          orderId={orderId}
          orderNumber={summary.order_number}
          currentGuestName={attachedGuestName}
        />
      )}

      {/* Move / transfer picker */}
      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Move Table {table.table_number}</DialogTitle>
            <DialogDescription>Pick an available table to move this order to.</DialogDescription>
          </DialogHeader>
          {freeTables.length === 0 ? (
            <p className="text-sm text-muted-foreground">No available tables to move to.</p>
          ) : (
            <div className="grid max-h-[50vh] grid-cols-3 gap-2 overflow-y-auto">
              {freeTables.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  disabled={reassignMutation.isPending}
                  onClick={() => reassignMutation.mutate(t.id)}
                  className="rounded-lg border border-input bg-card px-2 py-3 text-center transition-all hover:border-primary hover:shadow-sm disabled:opacity-50"
                >
                  <div className="font-semibold">{t.table_number}</div>
                  <div className="text-[11px] text-muted-foreground">{t.seating_capacity} seats</div>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  )

  // Portrait-tablet bottom panel: wide but short, so the bill and the actions
  // sit side by side instead of stacking.
  if (variant === 'sheet') {
    return (
      <div className="flex h-full min-h-0">
        <div className="flex min-w-0 flex-1 flex-col">
          {headerBlock}
          {billBlock}
        </div>
        <div className="flex w-[24rem] max-w-[48%] shrink-0 flex-col overflow-y-auto border-l border-border">
          {actionsBlock}
        </div>
        {dialogs}
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {headerBlock}
      {billBlock}
      {actionsBlock}
      {dialogs}
    </div>
  )
}
